"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Phone, Mail, MapPin, Star, CircleCheck, Loader as Loader2, CircleCheck as CheckCircle2 } from 'lucide-react';
const coverageAreas = ["مشاوره آنلاین (سراسر جهان)", "فارسی", "English", "العربية"];
const serviceOptions = ["اضطراب", "وسواس فکری-عملی", "افسردگی", "مشاوره کودک و نوجوان", "رابطه والد-فرزند", "مشاوره تحصیلی/شغلی", "روانشناسی میگرن/درد مزمن"];
const preferredTimeOptions = ["صبح (۹:۰۰ تا ۱۲:۰۰)", "بعدازظهر (۱۲:۰۰ تا ۱۶:۰۰)", "عصر و شب (۱۶:۰۰ تا ۲۰:۰۰)"];
function BookingFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center bg-accent/40 border border-border rounded-lg space-y-4">
        <CheckCircle2 className="size-12 text-primary mx-auto" />
        <h3 className="text-xl font-bold font-default text-foreground">
          درخواست شما با موفقیت ثبت شد
        </h3>
        <p className="text-muted-foreground font-default text-sm leading-relaxed max-w-md mx-auto">
          {successMessage || "از اعتماد شما سپاسگزاریم. همکاران ما در اولین فرصت جهت هماهنگی و تایید نوبت مشاوره با شما تماس خواهند گرفت."}
        </p>
        <div className="pt-2">
          <Button type="button" variant="outline" onClick={resetForm} className="rounded-lg font-default">
            ثبت درخواست جدید
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="name" rules={{
          required: "لطفاً نام و نام خانوادگی خود را وارد کنید",
          minLength: {
            value: 3,
            message: "نام باید حداقل ۳ حرف باشد"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground font-default text-sm">
                  نام و نام خانوادگی <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="مثال: سارا محمدی" className="rounded-lg font-default bg-background border-border" {...field} />
                </FormControl>
                <FormMessage className="text-xs font-default text-destructive" />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "لطفاً شماره تماس خود را وارد کنید",
          pattern: {
            value: /^(\+98|0)?9\d{9}$/,
            message: "شماره تماس معتبر وارد کنید (مثال: ۰۹۱۲۳۴۵۶۷۸۹)"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground font-default text-sm">
                  شماره تماس همراه <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="tel" dir="ltr" placeholder="0912XXXXXXX" className="rounded-lg font-default bg-background border-border text-right" {...field} />
                </FormControl>
                <FormMessage className="text-xs font-default text-destructive" />
              </FormItem>} />
        </div>

        <FormField control={control} name="email" rules={{
        required: "لطفاً ایمیل خود را وارد کنید",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "آدرس ایمیل معتبر نیست"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-default text-sm">
                آدرس ایمیل <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" dir="ltr" placeholder="example@domain.com" className="rounded-lg font-default bg-background border-border text-right" {...field} />
              </FormControl>
              <FormMessage className="text-xs font-default text-destructive" />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="serviceType" rules={{
          required: "لطفاً نوع خدمت درخواستی را مشخص کنید"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground font-default text-sm">
                  نوع خدمت مشاوره <span className="text-destructive">*</span>
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full rounded-lg font-default bg-background border-border text-right">
                      <SelectValue placeholder="انتخاب زمینه مشاوره" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="font-default rounded-lg">
                    {serviceOptions.map((service, index) => <SelectItem key={index} value={service}>
                        {service}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage className="text-xs font-default text-destructive" />
              </FormItem>} />

          <FormField control={control} name="preferredTime" rules={{
          required: "لطفاً زمان پیشنهادی خود را انتخاب کنید"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-muted-foreground font-default text-sm">
                  بازه زمانی ترجیحی <span className="text-destructive">*</span>
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full rounded-lg font-default bg-background border-border text-right">
                      <SelectValue placeholder="انتخاب زمان مناسب" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="font-default rounded-lg">
                    {preferredTimeOptions.map((time, index) => <SelectItem key={index} value={time}>
                        {time}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage className="text-xs font-default text-destructive" />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" rules={{
        required: "لطفاً شرح مختصری از علت مراجعه یا توضیحات لازم را بنویسید",
        minLength: {
          value: 10,
          message: "حداقل ۱۰ کاراکتر وارد نمایید"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-default text-sm">
                توضیحات یا شرح مختصر درخواست <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Textarea placeholder="لطفاً به اختصار درباره موضوع مشاوره یا شرایط مد نظرتان بنویسید..." rows={4} className="rounded-lg font-default bg-background border-border resize-y" {...field} />
              </FormControl>
              <FormMessage className="text-xs font-default text-destructive" />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg text-sm font-default">
            {submitError}
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full rounded-lg font-default bg-primary text-primary-foreground hover:bg-primary/90 h-11 text-base font-semibold shadow-md">
          {isSubmitting ? <span className="flex items-center justify-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              در حال ارسال درخواست...
            </span> : "ثبت و ارسال درخواست مشاوره"}
        </Button>
      </div>
    </Form>;
}
export default function DemanderUnEstime() {
  return <section data-section-id="254216" id="انتخاب-زمان-مشاوره" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mx-auto mb-4 bg-accent text-accent-foreground rounded-lg">
            رزرو آنلاین نوبت
          </Badge>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-4">
            همراه شما در مسیر آرامش و سلامت روان
          </h1>
          <p className="text-lg text-muted-foreground font-default">
            برای دریافت وقت مشاوره از دکتر مهرنوش اسماعیلی، لطفاً فرم زیر را تکمیل کنید. تیم ما در کوتاه‌ترین زمان ممکن برای هماهنگی نهایی با شما تماس خواهد گرفت.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-2 order-2 lg:order-1">
            <Card className="border-border shadow-lg rounded-lg overflow-hidden">
              <CardHeader className="bg-accent/30 border-b border-border pb-6">
                <CardTitle className="text-2xl font-default text-foreground">
                  فرم درخواست مشاوره
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="booking_form_book_appointment" sectionName="booking-section" defaultValues={{
                name: "",
                phone: "",
                email: "",
                serviceType: "",
                preferredTime: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 3
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                serviceType: {
                  required: true
                },
                preferredTime: {
                  required: true
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="ثبت و ارسال درخواست مشاوره" successMessage="درخواست وقت مشاوره شما با موفقیت ثبت گردید. به زودی با شما تماس خواهیم گرفت.">
                  <BookingFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1 order-1 lg:order-2 space-y-6">
            <Card className="border-border shadow-md bg-card rounded-lg">
              <CardContent className="p-6 space-y-8">
                <div>
                  <h3 className="text-lg font-default font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Phone className="size-5 text-secondary" />
                    راه‌های ارتباطی
                  </h3>
                  <div className="space-y-3 text-sm text-muted-foreground font-default">
                    <p className="flex items-center gap-3">
                      <Mail className="size-4 shrink-0" />
                      <span>contact@drmesmaili.com</span>
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-lg font-default font-semibold text-foreground mb-4 flex items-center gap-2">
                    <MapPin className="size-5 text-secondary" />
                    زبان‌ها و خدمات
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {coverageAreas.map((area, i) => <Badge key={i} data-index={i} variant="outline" className="bg-background rounded-lg font-default">
                        {area}
                      </Badge>)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border shadow-md bg-accent/50 rounded-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-1 mb-3 text-secondary">
                  {[1, 2, 3, 4, 5].map((star, i) => <Star key={i} data-index={i} className="size-5 fill-current" />)}
                </div>
                <p className="text-sm font-default font-medium text-foreground italic mb-2">
                  "رویکرد علمی و در عین حال صمیمانه خانم دکتر اسماعیلی به من کمک کرد تا با اضطراب‌هایم به درستی روبرو شوم."
                </p>
                <p className="text-xs text-muted-foreground font-semibold font-default">
                  — سارا م.، مراجع آنلاین
                </p>
                <div className="mt-4 flex items-center gap-2 text-sm font-medium text-foreground font-default">
                  <CircleCheck className="size-4 text-primary" />
                  بیش از ۱۰ سال تجربه بالینی
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}